// frontend/src/pages/crm/FieldAgentsPage.tsx
// Manager Field Agents view — Phase 4 (2026-07-29), spec §7 + §8. Mockup Plate 04.
//
// 🔒 ONE date control, exactly as FieldSessionsPage: a one-day range IS a single
// day. Two <input type="date"> feed the SAME request; `isSingleDay` only picks
// the heading wording, never which widget renders.
//
// 🔒 An axis with `target: null` renders "not set" — NEVER 0%. A target nobody
// entered is not a target the rep missed, and showing 0% would put every rep in
// the red on axes that were never being measured.
//
// 🔒 A zero entitlement is a REFERENCE, never "nothing to claim". The rep may
// have claimed ₹450 against an entitlement of ₹0.00, which is the normal state
// while the rate card is empty; both numbers are shown so the manager sees the
// gap and decides.
//
// 🔒 Permissions are enforced by the API (`view_field_agents` for reading,
// `approve_ta_da` for deciding). The page mirrors them for affordance only —
// `canApproveTaDa` comes from the server, and hiding a button is not security.
//
// ⚠️ NEVER RENDERED. The frontend does not compile locally (~70.9k pre-existing
// errors; react does not resolve), so this page is UNVERIFIED. It follows
// FieldSessionsPage.tsx structurally.
//
// Spec: docs/superpowers/specs/2026-07-29-field-activity-and-sessions-design.md

import { useState, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/utils/api';
// 🔒 Phase 5: the ladder, the formatters and the chips were EXTRACTED to
// fieldFormat.tsx so FieldAgentDetailPage and the rate card share them rather
// than each keeping a copy that can drift. Nothing here changed shape.
import {
  AxisChip, BTN_NO, BTN_OK, Empty, LadderTiles, Section, TD, TH, Tile,
  elapsed, fullName, km, pingAge, rupees, ymd,
  type Attainment,
} from './fieldFormat';
// 🔒 The null-vs-omitted rule lives in its own REACT-FREE module so it can
// actually be unit-tested (fieldTargetPatch.test.ts, 11 cases) — everything
// else on this page is unverifiable locally.
import { axisToInput, buildTargetPatch, type TargetAxisKey } from './fieldTargetPatch';

type PendingClaim = {
  id: string;
  startedAt: string;
  endedAt: string | null;
  measuredDistanceMetres: number | null;
  claimedDistanceMetres: number | null;
  entitlementPaise: number | null;
  claimedAmountPaise: number | null;
};

type AgentRow = {
  user: { id: string; firstName: string; lastName: string; email: string; designation: string | null };
  sessionCount: number;
  openSession: { id: string } | null;
  leadsCaptured: number;
  meetingsHeld: number;
  coloursAdvanced: number;
  daysOut: number;
  distanceMetres: number;
  claimedDistanceMetres: number;
  matrix: { red: number; yellow: number; orange: number; green: number; promoted: number };
  taDa: { draft: number; submitted: number; approved: number; rejected: number; none: number };
  attainment: Attainment;
  hasTarget: boolean;
  pendingClaims: PendingClaim[];
};

/** A row of the date-independent approval queue (GET /field-agents/pending-claims). */
type PendingRow = {
  session: {
    id: string;
    startedAt: string;
    endedAt: string | null;
    measuredDistanceMetres: number | null;
    claimedDistanceMetres: number | null;
    entitlementPaise: number | null;
    claimedAmountPaise: number | null;
  };
  user: { id: string; firstName: string; lastName: string } | null;
};

type LiveRow = {
  session: { id: string };
  user: { id: string; firstName: string; lastName: string } | null;
  startedAt: string;
  elapsedMinutes: number;
  startSource: string | null;
  lastPing: { lat: number; lng: number; recordedAt: string } | null;
};

export function FieldAgentsPage() {
  const today = ymd(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [live, setLive] = useState<LiveRow[]>([]);
  const [canApprove, setCanApprove] = useState(false);
  const [isSingleDay, setIsSingleDay] = useState(true);
  const [busyClaim, setBusyClaim] = useState<string | null>(null);
  /** Which rep's target form is open, if any. Phase 5. */
  const [targetFor, setTargetFor] = useState<string | null>(null);
  /** Phase 5 — every claim awaiting a decision, across ALL dates. */
  const [pending, setPending] = useState<PendingRow[]>([]);
  const [pendingTruncated, setPendingTruncated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [board, liveRes] = await Promise.all([
        api.get('/field-agents', { params: { from, to } }),
        // The live strip is independent of the date window — "out right now"
        // is always now. A failure here must not blank the board.
        api.get('/field-agents/live').catch(() => null),
      ]);
      const data = board.data?.data ?? {};
      setAgents(Array.isArray(data.agents) ? data.agents : []);
      setCanApprove(data.canApproveTaDa === true);
      setIsSingleDay(data.range?.isSingleDay !== false);
      const liveData = liveRes?.data?.data ?? {};
      setLive(Array.isArray(liveData.out) ? liveData.out : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not load field agents');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  /**
   * 🔒 Deliberately NOT part of `load` and NOT dependent on from/to. The
   * approval queue is not a property of the date window — a claim submitted
   * yesterday must still be visible when the board is showing today. A failure
   * here leaves the queue empty rather than blanking the board.
   */
  const loadPending = useCallback(async () => {
    try {
      const res = await api.get('/field-agents/pending-claims');
      const data = res.data?.data ?? {};
      setPending(Array.isArray(data.claims) ? data.claims : []);
      setPendingTruncated(data.truncated === true);
    } catch {
      setPending([]);
      setPendingTruncated(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPending(); }, [loadPending]);

  const decide = useCallback(
    async (sessionId: string, decision: 'approved' | 'rejected') => {
      let reason: string | null = null;
      if (decision === 'rejected') {
        // 🔒 A rejection reason is REQUIRED by the API. Prompting here keeps the
        // rep from receiving a bare "rejected" with no explanation.
        reason = window.prompt('Why is this claim rejected?');
        if (!reason || !reason.trim()) return;
      }
      setBusyClaim(sessionId);
      try {
        await api.post(`/field-ta-da/${sessionId}/decide`, { decision, reason });
        // Both: the queue loses the row, the board's per-rep counters change.
        await Promise.all([load(), loadPending()]);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Could not record the decision');
      } finally {
        setBusyClaim(null);
      }
    },
    [load, loadPending],
  );

  const heading = isSingleDay ? from : `${from} → ${to}`;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Field Agents</h1>
        <Link to="/field-sessions" style={{ fontSize: 14, color: '#6B7FD6' }}>My field sessions →</Link>
      </div>
      <p style={{ color: '#6b7280', marginTop: 0, fontSize: 13 }}>
        Who is out, what they captured and met, how far they travelled, and how they are
        tracking against target. Distance is measured from each rep's own location history,
        never typed from scratch.
      </p>

      {/* ── Live "who is out right now" strip ─────────────────────────────── */}
      <Section title="Out right now">
        {live.length === 0 ? (
          <Empty>Nobody is out right now.</Empty>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {live.map((l) => (
              <div
                key={l.session.id}
                style={{
                  padding: '10px 14px', borderRadius: 12, background: '#EAF6EF',
                  border: '1px solid #CBE7D8', minWidth: 200,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>{fullName(l.user)}</div>
                <div style={{ fontSize: 12, color: '#3A7A5A' }}>
                  out {elapsed(l.elapsedMinutes)}
                  {l.startSource === 'auto_field_lead' ? ' · auto-started' : ''}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  {l.lastPing
                    ? `last seen ${pingAge(l.lastPing.recordedAt)}`
                    : 'no location yet'}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── Awaiting your approval ────────────────────────────────────────
          🔒 ABOVE the date control on purpose, so it reads as visibly NOT
          subject to it. Every submitted claim among the people this manager
          can see, whatever day it was submitted on. Hidden entirely when the
          queue is empty — an empty strip would just be noise above the board.
          Gated on `approve_ta_da` (the server's own answer, from the board
          response), matching who may actually decide. */}
      {canApprove && pending.length > 0 && (
        <Section title={`Awaiting your approval (${pending.length})`}>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
            Every claim waiting on you, regardless of the dates selected below.
            {pendingTruncated ? ' Showing the 200 most recent — approve some to see the rest.' : ''}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}>
                <th style={TH}>Who</th>
                <th style={TH}>Day</th>
                <th style={TH}>Distance</th>
                <th style={TH}>Entitlement</th>
                <th style={TH}>Claimed</th>
                <th style={TH} />
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={p.session.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={TD}>{fullName(p.user)}</td>
                  <td style={TD}>{new Date(p.session.startedAt).toLocaleDateString()}</td>
                  <td style={TD}>
                    {km(p.session.claimedDistanceMetres ?? p.session.measuredDistanceMetres)}
                  </td>
                  {/* 🔒 Entitlement and claim SIDE BY SIDE so the gap is
                      visible. ₹0.00 entitlement is the normal state while the
                      rate card is empty — a reference, never a cap. */}
                  <td style={TD}>{rupees(p.session.entitlementPaise)}</td>
                  <td style={{ ...TD, fontWeight: 700 }}>{rupees(p.session.claimedAmountPaise)}</td>
                  <td style={TD}>
                    <button
                      disabled={busyClaim === p.session.id}
                      onClick={() => decide(p.session.id, 'approved')}
                      style={BTN_OK}
                    >
                      Approve
                    </button>
                    <button
                      disabled={busyClaim === p.session.id}
                      onClick={() => decide(p.session.id, 'rejected')}
                      style={BTN_NO}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* ONE date control. A one-day range IS a single day. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '20px 0 8px' }}>
        <input
          type="date"
          value={from}
          max={to}
          onChange={(e) => setFrom(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
        />
        <span style={{ color: '#9ca3af' }}>→</span>
        <input
          type="date"
          value={to}
          min={from}
          onChange={(e) => setTo(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
        />
        <button
          onClick={() => { setFrom(today); setTo(today); }}
          style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
        >
          Today
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>{heading}</span>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}
      {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}

      {!loading && agents.length === 0 && (
        <Empty>No field agents to show for this period.</Empty>
      )}

      {!loading && agents.map((a) => (
        <div
          key={a.user.id}
          style={{
            border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginTop: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            {/* Phase 5: the drill-down PAGE now exists, so this is a link.
                Same server gate as the board (`view_field_agents`), plus a
                per-person visibility check on the endpoint. */}
            <Link
              to={`/field-agents/${a.user.id}`}
              style={{ fontWeight: 700, fontSize: 16, color: '#111827', textDecoration: 'none' }}
            >
              {fullName(a.user)}
            </Link>
            {a.user.designation && (
              <span style={{ fontSize: 12, color: '#6b7280' }}>{a.user.designation}</span>
            )}
            {a.openSession && (
              <span style={{
                fontSize: 11, fontWeight: 700, color: '#3A7A5A',
                background: '#EAF6EF', padding: '2px 8px', borderRadius: 10,
              }}>
                OUT NOW
              </span>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            <Tile label="Leads captured" value={a.leadsCaptured} color="#6b7280" />
            <Tile label="Meetings held" value={a.meetingsHeld} color="#6b7280" />
            <Tile label="Colours advanced" value={a.coloursAdvanced} color="#6b7280" />
            <Tile label="Days out" value={a.daysOut} color="#6b7280" />
          </div>

          {/* 🔒 Colour bands via the shared renderer, so this page and the
              drill-down cannot disagree about the rungs or their colours. */}
          <LadderTiles matrix={a.matrix} />

          <p style={{ fontSize: 13, color: '#6b7280', margin: '10px 0 0' }}>
            {a.sessionCount} session{a.sessionCount === 1 ? '' : 's'} · measured {km(a.distanceMetres)} ·
            {' '}claimed {km(a.claimedDistanceMetres)}
          </p>

          {/* ── Target attainment ─────────────────────────────────────────── */}
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 11, letterSpacing: 0.6, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' }}>
                Targets
              </div>
              {/* Targets belong where the manager is already looking at the rep,
                  not on a separate admin page. */}
              <button
                onClick={() => setTargetFor(targetFor === a.user.id ? null : a.user.id)}
                style={BTN_LINK}
              >
                {targetFor === a.user.id ? 'Close' : a.hasTarget ? 'Edit target' : 'Set target'}
              </button>
            </div>

            {targetFor === a.user.id && (
              <TargetForm
                agent={a}
                monthAnchor={from}
                onCancel={() => setTargetFor(null)}
                onSaved={async () => { setTargetFor(null); await load(); }}
                onError={setError}
              />
            )}

            {!a.hasTarget ? (
              <Empty>No target set for this period.</Empty>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6 }}>
                <AxisChip label="Leads" a={a.attainment.leadsCaptured} />
                <AxisChip label="Meetings" a={a.attainment.meetingsHeld} />
                <AxisChip label="Colours advanced" a={a.attainment.coloursAdvanced} />
                <AxisChip label="Days out" a={a.attainment.daysOut} />
              </div>
            )}
          </div>

          {/* ── TA/DA ─────────────────────────────────────────────────────── */}
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, letterSpacing: 0.6, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' }}>
              Travel claims
            </div>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0' }}>
              {a.taDa.draft} draft · {a.taDa.submitted} awaiting you · {a.taDa.approved} approved ·
              {' '}{a.taDa.rejected} rejected
            </p>
            {a.pendingClaims.length === 0 ? (
              <Empty>Nothing awaiting a decision.</Empty>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}>
                    <th style={TH}>Day</th>
                    <th style={TH}>Distance</th>
                    <th style={TH}>Entitlement</th>
                    <th style={TH}>Claimed</th>
                    <th style={TH} />
                  </tr>
                </thead>
                <tbody>
                  {a.pendingClaims.map((c) => (
                    <tr key={c.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={TD}>{new Date(c.startedAt).toLocaleDateString()}</td>
                      <td style={TD}>{km(c.claimedDistanceMetres ?? c.measuredDistanceMetres)}</td>
                      {/* 🔒 ₹0.00 is a real, normal value here — the rate card
                          ships empty. It is shown so the manager can see the
                          gap, not as a cap. */}
                      <td style={TD}>{rupees(c.entitlementPaise)}</td>
                      <td style={{ ...TD, fontWeight: 700 }}>{rupees(c.claimedAmountPaise)}</td>
                      <td style={TD}>
                        {canApprove ? (
                          <>
                            <button
                              disabled={busyClaim === c.id}
                              onClick={() => decide(c.id, 'approved')}
                              style={BTN_OK}
                            >
                              Approve
                            </button>
                            <button
                              disabled={busyClaim === c.id}
                              onClick={() => decide(c.id, 'rejected')}
                              style={BTN_NO}
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          <span style={{ fontSize: 12, color: '#9ca3af' }}>view only</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const AXES = [
  { key: 'leadsCaptured', label: 'Leads captured', hint: null as string | null },
  { key: 'meetingsHeld', label: 'Meetings held', hint: null as string | null },
  {
    key: 'coloursAdvanced',
    label: 'Colour progression',
    // 🔒 The one axis nobody guesses. Without this line a manager reads
    // "colours advanced" as a colour-coded status count and sets it wrong.
    hint: 'Sites moved up the colour ladder — rewards working a site toward a decision maker, not just logging new ones.',
  },
  { key: 'daysOut', label: 'Days out', hint: null as string | null },
] as const;

function TargetForm({
  agent, monthAnchor, onCancel, onSaved, onError,
}: {
  agent: AgentRow;
  /** Any day in the month the board is showing — the API snaps it to the 1st. */
  monthAnchor: string;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
  onError: (msg: string) => void;
}) {
  const initial: Record<TargetAxisKey, string> = {
    leadsCaptured: axisToInput(agent.attainment?.leadsCaptured?.target),
    meetingsHeld: axisToInput(agent.attainment?.meetingsHeld?.target),
    coloursAdvanced: axisToInput(agent.attainment?.coloursAdvanced?.target),
    daysOut: axisToInput(agent.attainment?.daysOut?.target),
  };
  const [form, setForm] = useState<Record<TargetAxisKey, string>>(initial);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const patch = buildTargetPatch(initial, form);
    if (Object.keys(patch).length === 0) { onCancel(); return; }
    setSaving(true);
    try {
      await api.put('/field-targets', {
        userId: agent.user.id,
        period: 'month',
        // 🔒 The month the BOARD is showing, not today. The API snaps any day
        // in the month to its first IST midnight, which is the exact key the
        // board reads a target back by.
        periodStart: monthAnchor,
        ...patch,
      });
      await onSaved();
    } catch (e: any) {
      onError(e?.response?.data?.error || e?.message || 'Could not save the target');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 10, padding: 14, border: '1px solid #e5e7eb', borderRadius: 10, background: '#FAFAFB' }}>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
        Monthly target for {fullName(agent.user)}. Leave a box empty to leave that axis
        unmeasured — an empty box is not a target of zero.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {AXES.map((ax) => {
          const att = agent.attainment?.[ax.key];
          return (
            <div key={ax.key} style={{ minWidth: 190 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#374151', fontWeight: 600 }}>
                {ax.label}
              </label>
              <input
                inputMode="numeric"
                value={form[ax.key]}
                placeholder="not measured"
                onChange={(e) => setForm({ ...form, [ax.key]: e.target.value })}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 170, marginTop: 4 }}
              />
              {/* Current attainment beside the input, so the number is set
                  against reality rather than against a hunch. */}
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                so far this period: <strong>{att?.actual ?? 0}</strong>
                {att?.target != null ? ` · target ${att.target}` : ' · no target set'}
              </div>
              {ax.hint && (
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, maxWidth: 260 }}>{ax.hint}</div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12 }}>
        <button onClick={save} disabled={saving} style={BTN_OK}>
          {saving ? 'Saving…' : 'Save target'}
        </button>
        <button onClick={onCancel} disabled={saving} style={BTN_PLAIN}>Cancel</button>
      </div>
    </div>
  );
}

const BTN_PLAIN: CSSProperties = {
  padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600,
};

const BTN_LINK: CSSProperties = {
  padding: 0, border: 'none', background: 'none', cursor: 'pointer',
  color: '#6B7FD6', fontSize: 12, fontWeight: 600,
};
